import React, { useState } from "react";
import { Icon } from "@iconify/react";
import saveFill from "@iconify/icons-eva/save-outline";
import { validateRut, deconstructRut } from "@fdograph/rut-utilities";
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import {
  useGetClientesQuery,
  useAddSocioMutation,
} from "src/services/clientesApi";

const tipoSociosOptions = [
  { label: "Existente", value: "AUTOMATICO" },
  { label: "Manual", value: "MANUAL" },
];

export default function AgregarSocio({
  open,
  setOpen,
  refCliente,
  setShowMessage,
  setColor,
  setMessage,
}) {
  const [tipoSocio, setTipoSocio] = useState(tipoSociosOptions[0]);
  const [cliente, setCliente] = useState(null);
  const [clienteNombre, setClienteNombre] = useState("");

  const { data: clientes = [], isFetching: fetchingClientes } =
    useGetClientesQuery({ estado: "todos" });

  const [addSocio] = useAddSocioMutation();

  const handleClose = () => setOpen(false);

  const formik = useFormik({
    initialValues: {
      tipoSocio: "",
      rutSocio: "",
      nombreSocio: "",
    },
    validationSchema: Yup.object().shape({
      rutSocio: Yup.string().test(
        "rut test",
        "Rut no válido (ej: 12.345.678-9)",
        (value) => validateRut(value),
      ),
      nombreSocio: Yup.string().required(
        "Es necesario ingresar el nombre del socio",
      ),
    }),
    onSubmit: guardarDatos,
  });

  const {
    errors,
    touched,
    isSubmitting,
    setSubmitting,
    handleSubmit,
    getFieldProps,
    values,
  } = formik;

  async function guardarDatos() {
    const datosSocio = {
      tipoSocio: tipoSocio.value,
      refCliente,
      rutSocio:
        deconstructRut(values.rutSocio.toUpperCase()).digits +
        deconstructRut(values.rutSocio.toUpperCase()).verifier,
      nombreSocio: values.nombreSocio.toUpperCase(),
    };

    try {
      await addSocio(datosSocio).unwrap();
      setShowMessage(true);
      setColor("success");
      setMessage("Se han guardado los datos del socio.");
      handleClose();
    } catch {
      setShowMessage(true);
      setColor("error");
      setMessage("Ha ocurrido un error en el servidor o el socio ya existe.");
    } finally {
      setSubmitting(false);
    }
  }

  const handleTipoSocioChange = (_, newValue) => {
    if (!newValue) return;
    values.tipoSocio = newValue.value;
    values.nombreSocio = "";
    values.rutSocio = "";
    setTipoSocio(newValue);
  };

  const clientesFiltrados = clientes.filter((c) => c.rut !== refCliente);

  return (
    <Dialog
      open={open}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="form-dialog-title">Agregar un socio</DialogTitle>
      <DialogContent>
        <DialogContentText>Complete la información del socio</DialogContentText>

        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Autocomplete
              id="combo-tipo-socio"
              options={tipoSociosOptions}
              value={tipoSocio}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              getOptionLabel={(option) => option.label}
              onChange={handleTipoSocioChange}
              disableClearable
              style={{ marginBottom: 15, marginTop: 15 }}
              renderInput={(params) => (
                <TextField
                  required
                  {...params}
                  label="Seleccione tipo de ingreso"
                  {...getFieldProps("tipoSocio")}
                  error={Boolean(touched.tipoSocio && errors.tipoSocio)}
                  helperText={touched.tipoSocio && errors.tipoSocio}
                />
              )}
            />

            {tipoSocio.value === "AUTOMATICO" && (
              <Autocomplete
                value={cliente}
                options={clientesFiltrados}
                loading={fetchingClientes}
                isOptionEqualToValue={(option, value) =>
                  option.rut === value.rut
                }
                getOptionLabel={(option) => option.nombre}
                onChange={(_, newValue) => {
                  setCliente(newValue);
                  values.rutSocio = newValue?.rut ?? "";
                  values.nombreSocio = newValue?.nombre ?? "";
                }}
                inputValue={clienteNombre}
                onInputChange={(_, newInputValue) =>
                  setClienteNombre(newInputValue)
                }
                fullWidth
                ListboxProps={{ style: { maxHeight: "15rem" } }}
                renderOption={(props, option) => (
                  <li {...props} key={option.rut}>
                    {option.nombre}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Seleccione Cliente" />
                )}
              />
            )}

            {tipoSocio.value === "MANUAL" && (
              <>
                <TextField
                  required
                  fullWidth
                  type="text"
                  label="Nombre Socio"
                  {...getFieldProps("nombreSocio")}
                  style={{ marginBottom: 15 }}
                  error={Boolean(touched.nombreSocio && errors.nombreSocio)}
                  helperText={touched.nombreSocio && errors.nombreSocio}
                />
                <TextField
                  required
                  fullWidth
                  type="text"
                  label="Rut Socio"
                  placeholder="Ej: 12.345.678-9"
                  {...getFieldProps("rutSocio")}
                  style={{ marginBottom: 15 }}
                  error={Boolean(touched.rutSocio && errors.rutSocio)}
                  helperText={touched.rutSocio && errors.rutSocio}
                />
              </>
            )}

            <DialogActions>
              <LoadingButton onClick={handleClose} color="error">
                Cancelar
              </LoadingButton>
              <LoadingButton
                type="submit"
                color="primary"
                startIcon={<Icon icon={saveFill} />}
                loading={isSubmitting}
              >
                Guardar Socio
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
